import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Task } from './tasksApi.js';
import TaskForm from './TaskForm.js';
import { useSubtasks, useUpdateTask, useDeleteTask } from './useTasksQuery.js';
import { useUIStore } from '../../store/ui.store.js';
import { formatDueDate, isOverdue as isDueDateOverdue } from '../../lib/date.js';

interface Props {
  task: Task;
  onToggle: () => void;
  onDelete: () => void;
  isSubtask?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
}

const PRIORITY_COLORS: Record<Task['priority'], string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#6b7280',
};

function calcSubtaskSortOrder(tasks: Task[], activeId: string, overId: string): number {
  const oldIndex = tasks.findIndex((t) => t.taskId === activeId);
  const newIndex = tasks.findIndex((t) => t.taskId === overId);
  const reordered = arrayMove(tasks, oldIndex, newIndex);
  const prev = reordered[newIndex - 1];
  const next = reordered[newIndex + 1];
  if (!prev && !next) return Date.now();
  if (!prev) return (next.sortOrder ?? next.createdAt) + 1_000_000;
  if (!next) return (prev.sortOrder ?? prev.createdAt) - 1_000_000;
  return ((prev.sortOrder ?? prev.createdAt) + (next.sortOrder ?? next.createdAt)) / 2;
}

function SortableSubtaskCard({
  sub,
  parentTask,
  updateTask,
  deleteTask,
}: {
  sub: Task;
  parentTask: Task;
  updateTask: ReturnType<typeof useUpdateTask>;
  deleteTask: ReturnType<typeof useDeleteTask>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sub.taskId,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
    >
      <TaskCard
        task={sub}
        isSubtask
        dragHandleProps={{ ...attributes, ...listeners }}
        onToggle={() => updateTask.mutate({ taskId: sub.taskId, payload: { done: !sub.done } })}
        onDelete={() => deleteTask.mutate({ taskId: sub.taskId, parentTaskId: parentTask.taskId })}
      />
    </div>
  );
}

export default function TaskCard({ task, onToggle, onDelete, isSubtask = false, dragHandleProps }: Props) {
  const [editing, setEditing] = useState(false);
  const [subtasksOpen, setSubtasksOpen] = useState(false);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [activeSubId, setActiveSubId] = useState<string | null>(null);
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const sortMode = useUIStore((s) => s.sortMode);

  const { data: subtasks = [] } = useSubtasks(task.taskId, !isSubtask);
  const sortedSubtasks = [...subtasks].sort(
    (a, b) => (b.sortOrder ?? b.createdAt) - (a.sortOrder ?? a.createdAt)
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (editing) {
    return <TaskForm task={task} onClose={() => setEditing(false)} />;
  }

  const isOverdue = !task.done && task.dueDate && isDueDateOverdue(task.dueDate);
  const doneSubtasks = subtasks.filter((s) => s.done).length;
  const isManual = sortMode === 'manual';
  const activeSubTask = activeSubId ? sortedSubtasks.find((s) => s.taskId === activeSubId) : null;

  function handleSubDragStart({ active }: DragStartEvent) {
    setActiveSubId(active.id as string);
  }

  function handleSubDragEnd({ active, over }: DragEndEvent) {
    setActiveSubId(null);
    if (!over || active.id === over.id) return;
    const newSortOrder = calcSubtaskSortOrder(sortedSubtasks, active.id as string, over.id as string);
    updateTask.mutate({ taskId: active.id as string, payload: { sortOrder: newSortOrder } });
  }

  return (
    <div
      className={`task-card ${isSubtask ? 'task-card--subtask' : ''} ${task.done ? 'task-card--done' : ''} ${isOverdue ? 'task-card--overdue' : ''}`}
    >
      <div className="task-card__main">
        {dragHandleProps && isManual && (
          <span className="task-card__drag-handle" {...dragHandleProps} title="Drag to reorder">
            ⠿
          </span>
        )}
        <label className="task-card__checkbox-wrapper">
          <input
            type="checkbox"
            checked={task.done}
            onChange={onToggle}
            className="task-card__checkbox"
          />
          <span className="task-card__title">{task.title}</span>
        </label>
      </div>

      <div className="task-card__meta">
        <span className="task-card__priority" style={{ color: PRIORITY_COLORS[task.priority] }}>
          {task.priority}
        </span>

        {task.dueDate && (
          <span className={`task-card__due ${isOverdue ? 'task-card__due--overdue' : ''}`}>
            {formatDueDate(task.dueDate)}
          </span>
        )}

        {!isSubtask && subtasks.length > 0 && (
          <button className="task-card__subtask-count" onClick={() => setSubtasksOpen((o) => !o)}>
            {doneSubtasks}/{subtasks.length} subtasks
          </button>
        )}
      </div>

      {task.note && <p className="task-card__note">{task.note}</p>}

      <div className="task-card__actions">
        <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}>
          Edit
        </button>
        {!isSubtask && (
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => { setSubtasksOpen(true); setShowSubtaskForm(true); }}
          >
            + Subtask
          </button>
        )}
        <button className="btn btn--ghost btn--sm btn--danger" onClick={onDelete}>
          Delete
        </button>
      </div>

      {!isSubtask && (subtasksOpen || showSubtaskForm) && (
        <div className="task-card__subtasks">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleSubDragStart}
            onDragEnd={handleSubDragEnd}
          >
            <SortableContext
              items={sortedSubtasks.map((s) => s.taskId)}
              strategy={verticalListSortingStrategy}
            >
              {sortedSubtasks.map((sub) =>
                isManual ? (
                  <SortableSubtaskCard
                    key={sub.taskId}
                    sub={sub}
                    parentTask={task}
                    updateTask={updateTask}
                    deleteTask={deleteTask}
                  />
                ) : (
                  <TaskCard
                    key={sub.taskId}
                    task={sub}
                    isSubtask
                    onToggle={() => updateTask.mutate({ taskId: sub.taskId, payload: { done: !sub.done } })}
                    onDelete={() => deleteTask.mutate({ taskId: sub.taskId, parentTaskId: task.taskId })}
                  />
                )
              )}
            </SortableContext>

            <DragOverlay>
              {activeSubTask && (
                <div style={{ opacity: 0.9, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderRadius: 8 }}>
                  <TaskCard task={activeSubTask} isSubtask onToggle={() => {}} onDelete={() => {}} />
                </div>
              )}
            </DragOverlay>
          </DndContext>

          {showSubtaskForm && (
            <TaskForm
              listId={task.listId}
              parentTaskId={task.taskId}
              onClose={() => setShowSubtaskForm(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}
